/**
 * BlockWrapper - Kimi-style section wrapper for page preview blocks
 * 
 * Kimi-style Design:
 * - Clicking ANYWHERE on the section selects it
 * - Selection shows orange glow border
 * - Hover shows subtle highlight
 * - No persistent borders or containers
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Copy, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

// Floating toolbar for selected section
const SectionToolbar = ({ rect, onEdit, onDuplicate, onDelete, onMoveUp, onMoveDown }) => {
  if (!rect) return null;
  
  // Clamp toolbar position within viewport
  const toolbarHeight = 36;
  const top = Math.max(8, Math.min(rect.top + 8, window.innerHeight - toolbarHeight - 8));
  const left = Math.max(8, Math.min(rect.right - 160, window.innerWidth - 180));
  
  return createPortal(
    <div
      className="fixed z-[9999] flex items-center gap-0.5 bg-zinc-900/95 backdrop-blur-sm rounded-md border border-zinc-700/50 p-0.5 shadow-lg"
      style={{ top, left }}
      data-testid="section-toolbar"
    >
      {onMoveUp && (
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
          title="Move Up"
          data-testid="section-move-up"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
      )}
      {onMoveDown && (
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
          title="Move Down"
          data-testid="section-move-down"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="w-px h-4 bg-zinc-700/50 mx-0.5" />
      <button
        onClick={(e) => { e.stopPropagation(); onDuplicate?.(); }}
        className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
        title="Duplicate"
        data-testid="section-duplicate"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
        className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
        title="Delete"
        data-testid="section-delete"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>,
    document.body
  );
};

// Selection glow overlay
const SelectionGlow = ({ rect }) => {
  if (!rect) return null;
  
  return createPortal(
    <div
      className="fixed pointer-events-none z-[9998]"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }}
    >
      {/* Orange glow border - Kimi style */}
      <div 
        className="absolute inset-0"
        style={{
          boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.8), 0 0 20px rgba(249, 115, 22, 0.3)',
          borderRadius: '4px',
        }}
      />
    </div>,
    document.body
  );
};

// Section label pill
const SectionLabel = ({ rect, label }) => {
  if (!rect) return null;
  
  return createPortal(
    <div
      className="fixed z-[9997] px-2 py-1 bg-zinc-900/90 backdrop-blur-sm text-[10px] text-zinc-400 rounded border border-zinc-700/50"
      style={{
        bottom: window.innerHeight - rect.bottom + 8,
        right: window.innerWidth - rect.right + 8,
      }}
    >
      {label}
    </div>,
    document.body
  );
};

const BlockWrapper = ({ 
  blockId, 
  blockLabel,
  settingsKey,
  isSelected, 
  onSelect, 
  children,
  className = '',
  editorMode,
  onHoverChange,
  onDuplicate,
  onDelete,
  onMoveUp,
  onMoveDown,
  allowElementClicks = true, // WYSIWYG: Allow element-level clicks
}) => {
  const wrapperRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [rect, setRect] = useState(null);
  
  // Track element position for overlays
  const updateRect = useCallback(() => {
    if (wrapperRef.current && isSelected) {
      const r = wrapperRef.current.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        right: r.right,
        bottom: r.bottom,
        width: r.width,
        height: r.height,
      });
    } else {
      setRect(null);
    }
  }, [isSelected]);

  useEffect(() => {
    updateRect();
    
    if (!isSelected) return;
    
    window.addEventListener('scroll', updateRect, { passive: true });
    window.addEventListener('resize', updateRect);
    
    // Also listen to scroll on parent containers
    let parent = wrapperRef.current?.parentElement;
    const scrollParents = [];
    while (parent) {
      if (parent.scrollHeight > parent.clientHeight) {
        parent.addEventListener('scroll', updateRect, { passive: true });
        scrollParents.push(parent);
      }
      parent = parent.parentElement;
    }
    
    return () => {
      window.removeEventListener('scroll', updateRect);
      window.removeEventListener('resize', updateRect);
      scrollParents.forEach(p => p.removeEventListener('scroll', updateRect));
    };
  }, [isSelected, updateRect]);
  
  const handleMouseEnter = () => {
    setIsHovered(true);
    onHoverChange?.(blockId, true);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    onHoverChange?.(blockId, false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect(blockId);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        data-block-id={blockId}
        data-block-label={blockLabel}
        data-settings-key={settingsKey || undefined}
        data-testid={`block-wrapper-${blockId}`}
        className={`relative cursor-pointer ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {/* Hover indicator - subtle left edge */}
        {isHovered && !isSelected && (
          <div 
            className="absolute left-0 top-0 bottom-0 w-1 bg-blue-400/50 z-10 pointer-events-none"
            style={{ borderRadius: '0 2px 2px 0' }}
          />
        )}
        
        {/* Linked to Site Manager badge */}
        {settingsKey && (
          <div 
            className="absolute top-2 left-2 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-600/80 backdrop-blur-sm text-[9px] font-medium text-white pointer-events-none select-none"
            data-testid={`settings-link-badge-${blockId}`}
          >
            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Site Manager
          </div>
        )}
        
        {/* Content - allow pointer-events for WYSIWYG element selection */}
        <div className={`relative ${allowElementClicks ? '' : 'pointer-events-none'}`}>
          {children}
        </div>
      </div>
      
      {/* Selection overlays - rendered via portal */}
      {isSelected && rect && (
        <>
          <SelectionGlow rect={rect} />
          <SectionToolbar 
            rect={rect} 
            onEdit={() => {}}
            onDuplicate={() => onDuplicate?.(blockId)}
            onDelete={() => onDelete?.(blockId)}
            onMoveUp={onMoveUp ? () => onMoveUp(blockId) : null}
            onMoveDown={onMoveDown ? () => onMoveDown(blockId) : null}
          />
          <SectionLabel rect={rect} label={blockLabel} />
        </>
      )}
    </>
  );
};

export default BlockWrapper;
